import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { CSSTransition } from 'react-transition-group';
import { injectIntl, FormattedNumber } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import * as Icons from '@bibliocommons/deprecated-base-icons';

import { filterShape } from 'app/shapes/search';

import CheckboxFilter from '../CheckboxFilter';
import ExpandFilterLink from '../ExpandFilterLink';
import LinkFilter from '../LinkFilter';

import './SimpleField.scss';

const VISIBLE_FILTERS_LIMIT = 15;

export class SimpleField extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: this.props.initialIsExpanded
    };

    bindAll(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isExpanded && this.state.isExpanded && this._firstFilter && this._firstFilter._focusableElement) {
      this._firstFilter._focusableElement.focus();
    }
  }

  expandField(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState(state => ({ isExpanded: !state.isExpanded }));
  }

  static renderExpandIcon(expanded) {
    if (expanded) {
      return <Icons.Arrows.Medium.Up color="#333" className="small" />;
    }

    return <Icons.Arrows.Medium.Down color="#333" className="small" />;
  }

  static renderLabel(filterLabel, filter) {
    return (
      <span className="filter-label-text">
        {filterLabel}
        <span className="filter-count">
          (<FormattedNumber value={filter.get('count')} />)
        </span>
      </span>
    );
  }

  renderFilter(filter, index) {
    if (!filter) return null;
    const { fieldId, isConjunctive, group } = this.props;
    const FilterTag = isConjunctive ? LinkFilter : CheckboxFilter;
    const value = filter.get('value');
    const setFirstFilter = el => {
      if (index === 0) {
        this._firstFilter = el;
      }
    };

    return (
      <li className="filter" key={`${fieldId}-${group}-${value}`}>
        <FilterTag
          fieldId={this.props.fieldId}
          filter={filter}
          handleToggleFilter={this.props.handleToggleFilter}
          id={`SimpleField_${fieldId}_${value}`}
          refFunction={setFirstFilter}
        />
      </li>
    );
  }

  renderExpandFilterLink() {
    const { filters, hasMore } = this.props;
    const { isExpanded } = this.state;

    const isRenderLink = (hasMore || filters.size > VISIBLE_FILTERS_LIMIT) && isExpanded;

    return isRenderLink ? <ExpandFilterLink {...this.props} /> : null;
  }

  renderIcon() {
    const { iconName, icon } = this.props;

    if (typeof icon === 'object') {
      return icon;
    }

    if (iconName) {
      const IconComponent = Icons[iconName];

      return <IconComponent className="flip-vert" color="#333" />;
    }
    return <span aria-hidden className={icon} />;
  }

  render() {
    const { handleToggleExpandedField, intl, label, fieldId, filters, renderFilters } = this.props;

    const isIncludeAllFilters = !handleToggleExpandedField;

    return (
      <section className={`cp-simple-field clearfix ${fieldId} `}>
        <a
          href="#expand-filter"
          data-key={`${fieldId}-expand-filter`}
          onClick={this.expandField}
          className="field-toggle"
          aria-expanded={this.state.isExpanded}
        >
          {this.renderIcon()}
          <span className="field-name">{intl.formatMessage({ id: label })}</span>
          {SimpleField.renderExpandIcon(this.state.isExpanded)}
        </a>
        <CSSTransition
          in={this.state.isExpanded}
          classNames="fade"
          timeout={{ enter: 150, exit: 75 }}
          mountOnEnter
          unmountOnExit
        >
          {renderFilters ? (
            renderFilters(filters, this.renderFilter)
          ) : (
            <ul className="filter-list">
              {filters
                .filter(
                  (filter, index) => isIncludeAllFilters || index < VISIBLE_FILTERS_LIMIT || filter.get('applied')
                )
                .valueSeq()
                .map(this.renderFilter)}
            </ul>
          )}
        </CSSTransition>
        {!isIncludeAllFilters && this.renderExpandFilterLink()}
      </section>
    );
  }
}

SimpleField.propTypes = {
  activeFilters: PropTypes.object,
  fieldId: PropTypes.string,
  filters: ImmutablePropTypes.listOf(filterShape).isRequired,
  group: PropTypes.string,
  handleToggleExpandedField: PropTypes.func,
  handleToggleFilter: PropTypes.func,
  hasMore: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  iconName: PropTypes.string,
  initialIsExpanded: PropTypes.bool,
  intl: PropTypes.object,
  isConjunctive: PropTypes.bool,
  label: PropTypes.string,
  renderFilters: PropTypes.func
};

export default injectIntl(SimpleField);
